import {
    horizontalListSortingStrategy,
    SortableContext,
    useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useT } from "@magic-md/i18n/react";
import { Activity, SplitSquareHorizontal } from "lucide-react";
import { useCallback, useMemo, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { hasRunningTraceForTab } from "../lib/trace-paths";
import { cn } from "../lib/utils";
import { useEditorStore } from "../store/editor-store";
import { usePluginExecutionStore } from "../store/plugin-execution-store";
import { useTraceStore } from "../store/trace-store";
import { FileIcon, ProductIcon } from "../theme/runtime/theme-icons";
import { ContextMenu, useContextMenu } from "./ContextMenu";
import { buildTabContextMenuItems } from "./context-menu-builders";
import { DocumentRunAllStatusControl } from "./DocumentRunAllControl";
import type { TabDragData } from "./TabDndContext";

/**
 * Global TabBar — used in single-pane mode only.
 * In multi-pane mode, each PaneTabBar handles its own tabs.
 */
export function TabBar() {
    const t = useT("editor");
    const tabSignatures = useEditorStore(
        useShallow((s) =>
            s.tabs.map(
                (tab) =>
                    `${tab.id}\u0000${tab.title}\u0000${tab.isDirty ? "1" : "0"}`,
            ),
        ),
    );
    const setActiveTab = useEditorStore((s) => s.setActiveTab);
    const closeTab = useEditorStore((s) => s.closeTab);
    const splitPane = useEditorStore((s) => s.splitPane);

    const ctxMenu = useContextMenu();
    const ctxTabIdRef = useRef("");
    const [ctxTabId, setCtxTabId] = useState("");

    const tabs = useMemo(
        () =>
            tabSignatures.map((signature) => {
                const [id, title, dirtyFlag] = signature.split("\u0000");
                return {
                    id,
                    title,
                    isDirty: dirtyFlag === "1",
                };
            }),
        [tabSignatures],
    );

    const handleTabContextMenu = useCallback(
        (e: React.MouseEvent, tabId: string) => {
            ctxTabIdRef.current = tabId;
            setCtxTabId(tabId);
            ctxMenu.handler(e);
        },
        [ctxMenu],
    );

    if (tabs.length === 0) return null;

    const sortableIds = tabs.map((t) => `tab-global-${t.id}`);
    const menuItems = ctxTabId
        ? buildTabContextMenuItems({ tabId: ctxTabId, t, closeTab, tabs })
        : [];

    return (
        <div className="group/tabbar flex items-center h-8 bg-muted/40 border-b border-border overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden shrink-0">
            <SortableContext
                items={sortableIds}
                strategy={horizontalListSortingStrategy}
            >
                {tabs.map((tab) => {
                    return (
                        <StoreBackedDraggableTab
                            key={tab.id}
                            tabId={tab.id}
                            sourcePaneId=""
                            title={tab.title}
                            isDirty={tab.isDirty}
                            onClick={() => setActiveTab(tab.id)}
                            onClose={() => closeTab(tab.id)}
                            onContextMenu={(e) => handleTabContextMenu(e, tab.id)}
                        />
                    );
                })}
            </SortableContext>
            <div className="flex-1" />
            <TabBarSplitControls
                onSplitRight={() => splitPane("horizontal")}
                onSplitDown={() => splitPane("vertical")}
                revealClassName="group-hover/tabbar:opacity-100"
                testIdPrefix="global-tabbar"
            />
            <ContextMenu {...ctxMenu.props} items={menuItems} />
        </div>
    );
}

export function TabBarSplitControls({
    onSplitRight,
    onSplitDown,
    revealClassName,
    testIdPrefix,
}: {
    onSplitRight: () => void;
    onSplitDown: () => void;
    revealClassName: string;
    testIdPrefix?: string;
}) {
    const t = useT("editor");
    const buttonClassName =
        "flex h-full w-7 items-center justify-center text-muted-foreground transition-colors hover:bg-muted/60 hover:text-foreground";

    return (
        <div
            className={cn(
                "flex h-full shrink-0 items-center opacity-0 transition-opacity focus-within:opacity-100",
                revealClassName,
            )}
        >
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onSplitRight();
                }}
                className={buttonClassName}
                title={t("pane.splitRight")}
                aria-label={t("pane.splitRight")}
                data-testid={testIdPrefix ? `${testIdPrefix}-split-right` : undefined}
            >
                <SplitSquareHorizontal size={12} />
            </button>
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onSplitDown();
                }}
                className={cn(buttonClassName, "rotate-90")}
                title={t("pane.splitDown")}
                aria-label={t("pane.splitDown")}
                data-testid={testIdPrefix ? `${testIdPrefix}-split-down` : undefined}
            >
                <SplitSquareHorizontal size={12} />
            </button>
        </div>
    );
}

/* ── Single draggable tab button (shared by TabBar and PaneTabBar) ── */

export interface DraggableTabProps {
    tabId: string;
    sourcePaneId: string;
    isActive: boolean;
    isFocused?: boolean;
    isBackgroundRunning: boolean;
    isTraceRunning?: boolean;
    title: string;
    isDirty: boolean;
    onClick: () => void;
    onClose: () => void;
    onCancelExec: () => void;
    onContextMenu?: (e: React.MouseEvent) => void;
}

export type StoreBackedDraggableTabProps = Omit<
    DraggableTabProps,
    "isActive" | "isBackgroundRunning" | "onCancelExec"
> & {
    isActive?: boolean;
};

export function StoreBackedDraggableTab({
    isActive,
    tabId,
    ...props
}: StoreBackedDraggableTabProps) {
    const storeIsActive = useEditorStore((s) =>
        isActive === undefined ? s.activeTabId === tabId : false,
    );
    const tabFilePath = useEditorStore((s) =>
        s.tabs.find((tab) => tab.id === tabId)?.filePath ?? null,
    );
    const isBackgroundRunning = usePluginExecutionStore((s) =>
        s.backgroundRunningTabs.has(tabId),
    );
    const tabSessionId = usePluginExecutionStore((s) =>
        s.tabSessionMap.get(tabId) ?? null,
    );
    const isTraceRunning = useTraceStore((s) =>
        hasRunningTraceForTab(s.runs, {
            documentPath: tabFilePath,
            sessionId: tabSessionId,
        }),
    );
    const cancelBackgroundTabExecutions = usePluginExecutionStore(
        (s) => s.cancelBackgroundTabExecutions,
    );
    const handleCancelExec = useCallback(() => {
        cancelBackgroundTabExecutions(tabId);
    }, [cancelBackgroundTabExecutions, tabId]);

    return (
        <DraggableTab
            {...props}
            tabId={tabId}
            isActive={isActive ?? storeIsActive}
            isBackgroundRunning={isBackgroundRunning}
            isTraceRunning={isTraceRunning}
            onCancelExec={handleCancelExec}
        />
    );
}

export function DraggableTab({
    tabId,
    sourcePaneId,
    isActive,
    isFocused,
    isBackgroundRunning,
    isTraceRunning = false,
    title,
    isDirty,
    onClick,
    onClose,
    onCancelExec,
    onContextMenu,
}: DraggableTabProps) {
    const dragData: TabDragData = { type: "tab", tabId, sourcePaneId };
    const {
        attributes,
        listeners,
        setNodeRef,
        isDragging,
        transform,
        transition,
    } = useSortable({
        id: `tab-${sourcePaneId || "global"}-${tabId}`,
        data: dragData,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        ...(isActive && isFocused
            ? { borderTop: "2px solid var(--color-ring)" }
            : {}),
    };
    const sortableKeyDown = listeners?.onKeyDown;

    return (
        <div
            ref={setNodeRef}
            {...attributes}
            {...listeners}
            role="tab"
            aria-selected={isActive}
            onClick={onClick}
            onKeyDown={(e) => {
                sortableKeyDown?.(e);
                if (e.defaultPrevented) return;
                if (e.key === "Enter") {
                    e.preventDefault();
                    onClick();
                }
            }}
            onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onContextMenu?.(e);
            }}
            style={style}
            className={cn(
                "group flex items-center h-full px-3 text-xs border-r border-border transition-colors shrink-0 touch-none select-none cursor-default",
                isDragging && "opacity-40 z-50",
                isActive && isFocused
                    ? "bg-background text-foreground font-medium"
                    : isActive
                        ? "bg-background text-foreground"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/60",
            )}
        >
            <FileIcon name={title} size={12} className="mr-1.5 shrink-0" />
            <span className="max-w-[120px] truncate">
                {title.replace(/\.md$/i, "")}
            </span>
            {isTraceRunning && (
                <span
                    className="relative ml-1.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-info"
                    title="Trace running"
                    aria-label="Trace running"
                    data-testid={`tab-trace-running-${tabId}`}
                >
                    <span className="absolute h-2 w-2 rounded-full bg-info opacity-70 animate-ping" />
                    <Activity size={10} className="relative" strokeWidth={2.25} />
                </span>
            )}
            <DocumentRunAllStatusControl
                tabId={tabId}
                isBackgroundRunning={isBackgroundRunning}
                onCancelBackgroundRunning={onCancelExec}
            />
            {!isBackgroundRunning && isDirty && (
                <span className="ml-1.5 w-1.5 h-1.5 rounded-full bg-primary" />
            )}
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                }}
                className="ml-0 flex h-5 w-0 shrink-0 items-center justify-center overflow-hidden rounded p-0 opacity-0 transition-[width,margin,opacity,background-color] duration-150 group-hover:ml-1 group-hover:w-5 group-hover:opacity-100 group-focus-within:ml-1 group-focus-within:w-5 group-focus-within:opacity-100 hover:bg-muted-foreground/20"
            >
                <ProductIcon slot="tab.close" size={10} />
            </button>
        </div>
    );
}
